import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { FaPhoneSlash, FaVideo, FaPhone, FaCheck, FaTimes } from 'react-icons/fa';
import { auth, db } from '../../config/firebaseConfig';
import { useVideoCall } from '../Teleconference/VideoCallContext';
import {
  MISSED_CALL_HISTORY_MS,
  formatMissedCallTime,
  getCallTypeLabel,
  markMissedCallSeen,
  toDate,
} from '../../utils/missedCalls';
import { normalizeStoredName } from '../../utils/userIdentity';
import styles from './MissedCallsBell.module.css';

/**
 * Üst çubuktaki cevapsız arama bildirimi.
 *
 * Kaynak, `callRooms` koleksiyonunun kendisidir: kullanıcıya gelen ve
 * 'missed' durumuna düşmüş, henüz okunmamış odalar listelenir (bkz.
 * utils/missedCalls). Okundu işaretlenen kayıt listeden düşer.
 */
const MissedCallsBell = () => {
  const [missedCalls, setMissedCalls] = useState([]);
  const [open, setOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState(auth.currentUser?.email || null);
  const panelRef = useRef(null);
  const { initiateVideoCall, initiateVoiceCall } = useVideoCall();

  useEffect(() => onAuthStateChanged(auth, (user) => setAuthEmail(user?.email || null)), []);

  useEffect(() => {
    if (!authEmail) {
      setMissedCalls([]);
      return undefined;
    }

    const q = query(
      collection(db, 'callRooms'),
      where('inviteeEmail', '==', authEmail),
      where('status', '==', 'missed')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const cutoff = Date.now() - MISSED_CALL_HISTORY_MS;
        const rooms = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Okunmuş ve çok eski kayıtlar listeyi doldurmasın
          .filter((room) => !room.missedSeenAt)
          .filter((room) => {
            const at = toDate(room.missedAt || room.createdAt);
            return !at || at.getTime() >= cutoff;
          })
          .sort((a, b) => {
            const aAt = toDate(a.missedAt || a.createdAt)?.getTime() || 0;
            const bAt = toDate(b.missedAt || b.createdAt)?.getTime() || 0;
            return bAt - aAt;
          });
        setMissedCalls(rooms);
      },
      (error) => console.debug('Cevapsız aramalar okunamadı:', error)
    );

    return () => unsubscribe();
  }, [authEmail]);

  // Panel dışına tıklayınca kapansın
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const dismiss = useCallback(async (room) => {
    await markMissedCallSeen(room.roomId || room.id);
  }, []);

  const dismissAll = useCallback(async () => {
    await Promise.all(missedCalls.map((room) => markMissedCallSeen(room.roomId || room.id)));
    setOpen(false);
  }, [missedCalls]);

  const callBack = useCallback(
    async (room, type) => {
      await dismiss(room);
      setOpen(false);
      const start = type === 'voice' ? initiateVoiceCall : initiateVideoCall;
      await start(
        room.createdBy,
        normalizeStoredName(room.createdByName, room.createdBy),
        null
      );
    },
    [dismiss, initiateVideoCall, initiateVoiceCall]
  );

  if (missedCalls.length === 0) return null;

  return (
    <div className={styles.wrapper} ref={panelRef}>
      <button
        type="button"
        className={`icon-button notification-button has-unseen ${styles.bellButton}`}
        onClick={() => setOpen((prev) => !prev)}
        title={`${missedCalls.length} cevapsız arama`}
        aria-label={`${missedCalls.length} cevapsız arama`}
      >
        <FaPhoneSlash />
        <span className="notification-badge">
          {missedCalls.length > 99 ? '99+' : missedCalls.length}
        </span>
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>
              Cevapsız Aramalar ({missedCalls.length})
            </span>
            <button type="button" className={styles.panelClear} onClick={dismissAll}>
              Tümünü okundu işaretle
            </button>
          </div>

          <ul className={styles.list}>
            {missedCalls.map((room) => (
              <li key={room.id} className={styles.item}>
                <span className={styles.itemIcon}>
                  {(room.callType || room.type) === 'voice' ? <FaPhone /> : <FaVideo />}
                </span>
                <div className={styles.itemInfo}>
                  <span className={styles.itemName}>
                    {normalizeStoredName(room.createdByName, room.createdBy)}
                  </span>
                  {room.createdBy && (
                    <span className={styles.itemEmail}>{room.createdBy}</span>
                  )}
                  <span className={styles.itemMeta}>
                    Cevapsız {getCallTypeLabel(room.callType || room.type)} ·{' '}
                    {formatMissedCallTime(room.missedAt || room.createdAt)}
                  </span>
                </div>
                <div className={styles.itemActions}>
                  <button
                    type="button"
                    className={styles.itemAction}
                    onClick={() => callBack(room, 'voice')}
                    title="Sesli geri ara"
                    aria-label="Sesli geri ara"
                  >
                    <FaPhone />
                  </button>
                  <button
                    type="button"
                    className={styles.itemAction}
                    onClick={() => callBack(room, 'video')}
                    title="Görüntülü geri ara"
                    aria-label="Görüntülü geri ara"
                  >
                    <FaVideo />
                  </button>
                  <button
                    type="button"
                    className={`${styles.itemAction} ${styles.itemActionMuted}`}
                    onClick={() => dismiss(room)}
                    title="Okundu işaretle"
                    aria-label="Okundu işaretle"
                  >
                    <FaCheck />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={styles.panelClose}
            onClick={() => setOpen(false)}
            aria-label="Kapat"
          >
            <FaTimes /> Kapat
          </button>
        </div>
      )}
    </div>
  );
};

export default MissedCallsBell;
